import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  TablePagination,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { newServer } from 'constants/constants';
import EditIcon from '@mui/icons-material/Edit';
import { Header } from 'components/Header/Header';
import moment from 'moment';
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select } from '@mui/material';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';

interface Subscription {
  subscribe_rate_id: number;
  id: number;
  name: string;
  subscription_status: string;
  date_created: string;
  marketing_title: string;
  date_updated: string;
  balance: string;
  subscription_cancel_at_period_end: string;
  trial_start: string;
  submerchant_id: number;
  info_usio: {
    legalName: string;
  };
}

const SubscriptionsSubmerchant: React.FC = () => {
  const { user, logout } = useLogin();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedSub, setSelectedSub] = useState<Subscription | null>(null);
  const [subscriptionsData, setSubscriptionsData] = useState<Subscription[]>([]);
  const [subscriptionsSubmerchant, setSubscriptionsSubmerchant] = useState<Subscription[]>([]); //
  const [submerchantRequest, setSubmerchantRequest] = useState<Subscription[]>([]);
  const token = localStorage.getItem('token');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSubmerchant, setSelectedSubmerchant] = useState<number | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<number | null>(null);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleCreateSubscription = () => {
    if (selectedSubscription !== null && selectedSubmerchant !== null) {
      handleSelectSubscription(selectedSubscription, selectedSubmerchant);
    }
    handleCloseModal();
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm')); // Si quieres que el modal sea fullscreen en móviles

  const handleOpenDrawer = (sub: Subscription, edit: boolean) => {
    if (edit) {
      setSelectedSub(sub);
      setOpenDrawer(true);
    } else {
      setSelectedSub(null);
      setOpenDrawer(true);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Data segmentada para la paginación
  const paginatedData = subscriptionsSubmerchant.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSelectSubscription = async (
    selectedSubscription: number,
    selectedSubmerchant: number
  ) => {
    try {
      const response = await fetch(`${newServer}subscriptions/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subscribe_rate_id: selectedSubscription,
          submerchant_id: selectedSubmerchant,
          subdomain: '',
        }),
      });
      const result = await response.json();
      getSubscription();
    } catch (error) {
      console.error('Error selecting subscription:', error);
    }
  };

  const getSubscription = async () => {
    try {
      const response = await fetch(`${newServer}subscriptions/all`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.status === 'JWT_EXPIRED') {
        logout();
        return;
      }
      setSubscriptionsData(data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const getSubscriptionSubmerchant = async () => {
    try {
      const response = await fetch(`${newServer}subscriptions/submerchant/all`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.status === 'JWT_EXPIRED') {
        logout();
        return;
      }
      setSubscriptionsSubmerchant(data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const getSubmerchantRequest = async () => {
    try {
      const response = await fetch(`${newServer}subscriptions/submerchant-finish`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (data.status === 'JWT_EXPIRED') {
        logout();
        return;
      }

      setSubmerchantRequest(data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getSubscription();
    getSubscriptionSubmerchant();
    getSubmerchantRequest();
  }, []);

  return (
    <>
      <Header />
      <Box sx={{ p: 3, bgcolor: 'white', height: '100vh' }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom sx={{ color: 'black' }}>
          Subscription Submerchant
        </Typography>
        <Divider sx={{ mb: 3, borderColor: 'black' }} />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
            onClick={handleOpenModal}
          >
            Create
          </Button>
        </Box>

        <Dialog open={openModal} onClose={handleCloseModal} fullScreen={fullScreen}>
          <DialogTitle>Select Submerchant and Subscription</DialogTitle>
          <DialogContent>
            <Select
              value={selectedSubmerchant}
              onChange={(e) => setSelectedSubmerchant(e.target.value as unknown as number)}
              displayEmpty
              fullWidth
            >
              <MenuItem value='' disabled>
                Select Submerchant
              </MenuItem>
              {submerchantRequest.map((subscription) => (
                <MenuItem key={subscription.submerchant_id} value={subscription.submerchant_id}>
                  {subscription.submerchant_id} - {subscription.info_usio.legalName} -{' '}
                  {subscription.name}
                </MenuItem>
              ))}
            </Select>
            <br />
            <br />
            <Select
              value={selectedSubscription}
              onChange={(e) => setSelectedSubscription(e.target.value as unknown as number)}
              displayEmpty
              fullWidth
            >
              <MenuItem value='' disabled>
                Select Subscription
              </MenuItem>
              {subscriptionsData.map((subscription) => (
                <MenuItem
                  key={subscription.subscribe_rate_id}
                  value={subscription.subscribe_rate_id}
                >
                  {subscription.marketing_title}
                </MenuItem>
              ))}
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color='primary'>
              Cancel
            </Button>
            <Button onClick={handleCreateSubscription} color='primary'>
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Contenedor responsive para la tabla */}
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer
            component={Paper}
            sx={{ bgcolor: 'white', border: '1px solid black', minWidth: 600 }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'black' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Subscriptions ID.
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Subcriptions</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Submerchant Name
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Active</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Balance</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Trial Update</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Trial of Expiration
                  </TableCell>
                  <TableCell align='right' sx={{ color: 'white', fontWeight: 'bold' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((sub) => (
                  <TableRow
                    key={sub.id}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                      '&:hover': { backgroundColor: '#e0e0e0' },
                    }}
                  >
                    <TableCell sx={{ color: 'black' }}>{sub.id}</TableCell>
                    <TableCell sx={{ color: 'black' }}>{sub.marketing_title}</TableCell>
                    <TableCell sx={{ color: 'black' }}>{sub.name}</TableCell>
                    <TableCell sx={{ color: 'black' }}>{sub.subscription_status}</TableCell>
                    <TableCell sx={{ color: 'black' }}>
                      {moment(sub.date_updated).format('MM-DD-YYYY HH:mm')}
                    </TableCell>
                    <TableCell sx={{ color: 'black' }}>
                      {sub.balance ? sub.balance : '0.00'}
                    </TableCell>
                    <TableCell sx={{ color: 'black' }}>
                      {sub.trial_start ? moment(sub.trial_start).format('MM-DD-YYYY HH:mm') : ''}
                    </TableCell>
                    <TableCell sx={{ color: 'black' }}>
                      {sub.subscription_cancel_at_period_end
                        ? moment(sub.subscription_cancel_at_period_end).format('MM-DD-YYYY HH:mm')
                        : ''}
                    </TableCell>
                    <TableCell align='right'>
                      <Button
                        variant='contained'
                        size='small'
                        sx={{
                          backgroundColor: 'black',
                          color: 'white',
                          mr: 1,
                          '&:hover': {
                            backgroundColor: '#333',
                          },
                        }}
                        onClick={() => {
                          handleOpenDrawer(sub, true);
                        }}
                      >
                        <EditIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align='center' sx={{ color: 'black' }}>
                      No data available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component='div'
              count={subscriptionsSubmerchant.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: '1px solid black', color: 'black' }}
            />
          </TableContainer>
        </Box>
      </Box>
    </>
  );
};

export default SubscriptionsSubmerchant;
